import React, { createContext, useState, useContext } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';


const CartContext = createContext();

// Helper function to generate a unique key for each item + variant + note combo
export const generateKey = (item) => {
    return `${item.id}__${item.variantKey || ''}__${item.note || ''}`;
};

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const { t } = useTranslation();


    const addToCart = (item) => {
        setCartItems(prev => {
            const key = generateKey(item);
            const existingIndex = prev.findIndex(i => generateKey(i) === key);

            if (existingIndex !== -1) {
                const updated = [...prev];
                updated[existingIndex].quantity += 1;
                return updated;
            } else {
                return [...prev, { ...item, quantity: 1 }];
            }
        });
        toast.success(`🛒 ${t('cart.itemAdded')}`);
    };

    const removeFromCart = (itemKey) => {
        setCartItems(prev => prev.filter(item => generateKey(item) !== itemKey));
        toast.info(`🗑️ ${t('cart.itemRemoved')}`);
    };

    const increaseQuantity = (itemKey) => {
        setCartItems(prev =>
            prev.map(item =>
                generateKey(item) === itemKey ? { ...item, quantity: item.quantity + 1 } : item
            )
        );
    };

    const decreaseQuantity = (itemKey) => {
        setCartItems(prev =>
            prev.map(item =>
                generateKey(item) === itemKey && item.quantity > 1
                    ? { ...item, quantity: item.quantity - 1 }
                    : item
            )
        );
    };

    const updateNote = (itemKey, newNote) => {
        setCartItems(prev =>
            prev.map(item =>
                generateKey(item) === itemKey ? { ...item, note: newNote } : item
            )
        );
    };

    const clearCart = () => {
        setCartItems([]);
    };

    // Calculate total
    const total = cartItems.reduce((sum, item) => {
        const price = typeof item.price === 'number' ? item.price : 0;
        return sum + price * item.quantity;
    }, 0);

    return (
        <CartContext.Provider value={{
            cartItems,
            addToCart,
            removeFromCart,
            increaseQuantity,
            decreaseQuantity,
            updateNote,
            clearCart,
            total
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
